<template>
    <div class="ulvm-overlay" @click.self="onOverlayClick">
        <div class="ulvm-container">
            <!-- Close button -->
            <div class="ulvm-close-row">
                <button class="ulvm-close-btn" @click="$emit('close')" aria-label="Close video">
                    <i class="mdi mdi-close" style="font-size: 24px;" />
                </button>
            </div>

            <!-- Video wrapper — fullscreen targets this element -->
            <div style="position: relative;" ref="vimeoContainer">
                <vimeo-player
                    ref="player"
                    :options="{ responsive: true, height: 420, maxheight: 420, pip: false }"
                    :video-url="videoUrl"
                    :controls="false"
                    @loaded="onLoaded"
                    @ended="onVideoEnded"
                    @timeupdate="onTimeUpdate"
                    @seeked="onSeeked"
                    class="video-wrapper"
                />

                <!-- Custom Controls — mirrors tutorial.vue exactly -->
                <div :class="`d-flex align-center videoControls ${css_fullscreen_controls}`">

                    <!-- Play / Pause + Rewind -->
                    <div class="d-flex justify-space-between">
                        <v-btn ref="play" icon class="playButton" @click="togglePlayback">
                            <v-icon v-if="playing">mdi-pause</v-icon>
                            <v-icon v-else>mdi-play</v-icon>
                        </v-btn>
                        <v-btn @click="rewindVideo" class="rewindButton autoplay-button" title="Back 5 seconds" aria-label="Back 5 seconds">
                            <v-icon>mdi-rewind-5</v-icon>
                        </v-btn>
                    </div>

                    <!-- Volume -->
                    <div class="volume-container volumeControls" @mouseleave="hideVolumeSlider" @mouseenter="showVolumeSlider">
                        <v-icon @click="muteUnmute" class="mr-2">
                            {{ Unmute ? 'mdi-volume-high' : 'mdi-volume-off' }}
                        </v-icon>
                        <div v-show="isVolumeSliderVisible" class="volume-slider-container" @mouseenter="showVolumeSlider" @mouseleave="hideVolumeSlider">
                            <div class="custom-volume-slider" @click="onSliderClick" ref="volumeSlider">
                                <div class="slider-track"></div>
                                <div class="slider-fill" :style="{ height: volume + '%' }"></div>
                                <div
                                    class="slider-thumb"
                                    :style="{ bottom: volume + '%' }"
                                    @mousedown="startDrag"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <!-- Video Duration Watched (spacer) -->
                    <div class="videoDurationWatched" />
                    <div class="d-flex justify-space-between videoDurationWidth">
                        <p v-if="videoDuration" class="mb-0 mr-3 videoDuration">
                            {{ formattedVideoProgress || '00:00' }}
                        </p>
                    </div>

                    <!-- Progress bar (pointer-events: none prevents seeking) + total duration -->
                    <div :class="`videoProgressBar ${css_video_progressbar}`">
                        <v-progress-linear height="16" color="primary" :value="videoPercent" style="border-radius: 10px !important; pointer-events: none;" />
                        <p v-if="videoDuration" class="mb-0 ml-3 videoDuration">
                            {{ formattedVideoDuration }}
                        </p>
                    </div>

                    <!-- Fullscreen toggle -->
                    <div class="d-flex justify-space-between">
                        <v-btn v-if="!isFullscreen" @click="goFullscreen" class="fullscreen-button autoplay-button" title="Fullscreen" aria-label="Fullscreen">
                            <v-icon>mdi-fullscreen</v-icon>
                        </v-btn>
                        <v-btn v-if="isFullscreen" @click="goExitFullscreen" class="fullscreen-button autoplay-button" title="Exit Fullscreen" aria-label="Exit Fullscreen">
                            <v-icon>mdi-fullscreen-exit</v-icon>
                        </v-btn>
                    </div>

                </div>
            </div>

            <!-- Watch-notice shown while video is incomplete -->
            <p v-if="!videoCompleted" class="ulvm-notice">
                <v-icon small class="mr-1">mdi-lock</v-icon>
                Please watch the entire video to continue
            </p>
        </div>
    </div>
</template>

<script>
import { vueVimeoPlayer } from 'vue-vimeo-player'
import { mapFields } from 'vuex-map-fields'

/**
 * Programmatically-mountable modal for university onboarding lesson videos.
 * Controls are a faithful port of tutorial.vue — same variable names, computed
 * properties, HTML structure, and CSS classes — with the no-skip-ahead logic
 * layered on top via onTimeUpdate / onSeeked.
 *
 */
export default {
    name: 'UniversityLessonVideoModal',

    components: {
        vimeoPlayer: vueVimeoPlayer,
    },

    props: {
        /** Always true when mounted; kept for API compatibility with createVideoModal. */
        show: {
            type: Boolean,
            default: true,
        },
        videoUrl: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            // ── playback state (mirrors tutorial.vue naming) ──
            playing: false,
            videoSeconds: null,
            videoDuration: null,
            videoProgress: null,  // 0-1 percent

            // ── volume state (mirrors tutorial.vue naming) ──
            volume: 50,
            volumeLevel: null,
            mute: false,
            Unmute: true,
            isVolumeSliderVisible: false,
            isDragging: false,

            // ── fullscreen state (mirrors tutorial.vue naming) ──
            isFullscreen: false,
            css_fullscreen_controls: '',
            css_video_progressbar: '',

            /** Furthest timestamp the user has watched; prevents skip-ahead. */
            lastTime: 0,
            /** Guard against double-firing onVideoEnded. */
            endFired: false,
        }
    },

    computed: {
        ...mapFields('onboarding_state', ['videoCompleted', 'currentLessonData']),

        /** Matches tutorial.vue's videoPercent computed. */
        videoPercent() {
            return this.videoProgress * 100
        },

        /** Matches tutorial.vue's formattedVideoDuration computed. */
        formattedVideoDuration() {
            let minutes = Math.floor(this.videoDuration / 60)
            let seconds = Math.floor(this.videoDuration % 60)
            return minutes + ':' + String(seconds).padStart(2, '0')
        },

        /** Matches tutorial.vue's formattedVideoProgress computed. */
        formattedVideoProgress() {
            let currentSeconds = this.videoProgress * this.videoDuration
            let minutes = Math.floor(currentSeconds / 60)
            let seconds = Math.floor(currentSeconds % 60)
            return minutes + ':' + String(seconds).padStart(2, '0')
        },
    },

    mounted() {
        document.addEventListener('fullscreenchange', this.handleFullscreenChange)
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    },

    methods: {
        // ── Vimeo player events ────────────────────────────────────────────

        onLoaded(evt, data) {
            if (data?.duration) this.videoDuration = data.duration

            setTimeout(() => {
                if (this.$refs.player?.player) {
                    const level = (this.volume / 100).toFixed(2)
                    this.volumeLevel = level
                    this.$refs.player.player.setVolume(level)
                    this.$refs.player.play()
                        .then(() => { this.playing = true })
                        .catch(() => {})
                }
            }, 200)
        },

        /**
         * Tracks playback and enforces no-skip-ahead.
         * If seconds jumps > 1.5 s past lastTime the player snaps back.
         * Mirrors tutorial.vue's onTimeUpdate but adds the skip-prevention layer.
         */
        onTimeUpdate(data) {
            this.videoSeconds = data.seconds
            this.videoProgress = data.percent
            this.videoDuration = data.duration

            if (!this.videoCompleted && this.videoDuration) {
                // Near-end tolerance — treat as complete
                if (this.videoDuration - data.seconds <= 0.5 && !this.endFired) {
                    this.onVideoEnded()
                    return
                }

                // User tried to skip ahead — snap back to last legitimate position
                if (data.seconds > this.lastTime + 1.5) {
                    this.$refs.player?.player?.setCurrentTime(this.lastTime)
                    return
                }

                if (data.seconds >= this.lastTime) {
                    this.lastTime = data.seconds
                }
            }
        },

        /**
         * Fires when Vimeo's internal seek completes.
         * Resets to lastTime while the video isn't yet marked complete.
         * Mirrors tutorial.vue's onSeeked but adds skip-prevention.
         */
        onSeeked(evt) {
            if (evt?.percent) {
                this.videoProgress = evt.percent
                this.videoDuration = evt.duration
                this.videoSeconds = evt.seconds
            }
            if (!this.videoCompleted && this.$refs.player?.player) {
                this.$refs.player.player.setCurrentTime(this.lastTime)
            }
        },

        /**
         * Called when the video reaches the end (or near-end via onTimeUpdate).
         * Marks the course lesson as completed via the API.
         */
        async onVideoEnded() {
            if (this.endFired) return
            this.endFired = true
            this.playing = false

            this.$store.commit('onboarding_state/setVideoCompleted', true)

            if (this.currentLessonData && !this.currentLessonData.isCompleted) {
                try {
                    await this.$http.post(
                        '/api/v1/course-complete/markCourseLessonAsCompleted/' + this.currentLessonData.lessonId,
                        { task_id: this.currentLessonData.taskId }
                    )
                    this.currentLessonData.isCompleted = true
                    this.$root.$emit('onboarding-lesson-video-completed')
                } catch (err) {
                    console.error('Error marking lesson as completed:', err)
                }
            }
        },

        // ── Playback controls (mirrors tutorial.vue) ──────────────────────

        togglePlayback() {
            if (this.playing) {
                this.$refs.player.pause()
            } else {
                this.$refs.player.play()
            }
            this.playing = !this.playing
        },

        /** Mirrors tutorial.vue's rewindVideo exactly. */
        rewindVideo() {
            const rewind = 5
            let currentTime = Math.floor(this.videoProgress * this.videoDuration)
            currentTime = Math.max(currentTime, rewind + 1)
            // Pull lastTime back so the no-skip guard allows the rewind
            this.lastTime = currentTime - rewind - 1
            this.$refs.player.player.setCurrentTime(currentTime - rewind)
        },

        // ── Volume controls (mirrors tutorial.vue) ────────────────────────

        /** Mirrors tutorial.vue's muteUnmute exactly. */
        muteUnmute() {
            if (this.volumeLevel === 0 || this.volume == 0) {
                this.volume = 50
                const volumeLevel = (this.volume / 100).toFixed(2)
                this.volumeLevel = volumeLevel
                this.$refs.player.player.setVolume(volumeLevel)
                this.Unmute = true
                this.mute = false
            } else if (this.volumeLevel > 0 || this.volume > 0) {
                this.Unmute = false
                this.mute = true
                this.volume = 0
                this.$refs.player.player.setVolume(0)
            }
        },


        showVolumeSlider() {
            if (this._volumeHideTimer) {
                clearTimeout(this._volumeHideTimer)
                this._volumeHideTimer = null
            }
            this.isVolumeSliderVisible = true
        },


        hideVolumeSlider() {
            this._volumeHideTimer = setTimeout(() => {
                this.isVolumeSliderVisible = false
                this._volumeHideTimer = null
            }, 150)
        },

        /** Mirrors tutorial.vue's onVolumeChange exactly. */
        onVolumeChange() {
            if (this.$refs.player) {
                const volumeLevel = this.volume / 100
                this.volumeLevel = volumeLevel
                this.$refs.player.player.setVolume(volumeLevel)
                this.mute = volumeLevel === 0
                this.Unmute = volumeLevel > 0
            }
        },

        onSliderClick(event) {
            const rect = this.$refs.volumeSlider.getBoundingClientRect()
            const y = event.clientY - rect.top
            const height = rect.height
            const newVolume = Math.max(0, Math.min(100, ((height - y) / height) * 100))
            this.volume = Math.round(newVolume)
            this.onVolumeChange()
        },

        startDrag(event) {
            this.isDragging = true
            document.addEventListener('mousemove', this.onDrag)
            document.addEventListener('mouseup', this.stopDrag)
            event.preventDefault()
        },

        onDrag(event) {
            if (!this.isDragging) return
            const rect = this.$refs.volumeSlider.getBoundingClientRect()
            const y = event.clientY - rect.top
            const height = rect.height
            const newVolume = Math.max(0, Math.min(100, ((height - y) / height) * 100))
            this.volume = Math.round(newVolume)
            this.onVolumeChange()
        },

        stopDrag() {
            this.isDragging = false
            document.removeEventListener('mousemove', this.onDrag)
            document.removeEventListener('mouseup', this.stopDrag)
        },

        // ── Fullscreen (mirrors tutorial.vue) ────────────────────────────

        /** Mirrors tutorial.vue's goFullscreen exactly. */
        goFullscreen() {
            const elem = this.$refs.vimeoContainer
            if (elem.requestFullscreen) {
                elem.requestFullscreen()
            } else if (elem.webkitRequestFullscreen) {
                elem.webkitRequestFullscreen()
            } else if (elem.webkitEnterFullscreen) {
                elem.webkitEnterFullscreen()
            } else if (elem.msRequestFullscreen) {
                elem.msRequestFullscreen()
            } else if (elem.mozRequestFullScreen) {
                elem.mozRequestFullScreen()
            }
        },

        /** Mirrors tutorial.vue's goExitFullscreen exactly. */
        goExitFullscreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen()
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen()
            } else if (document.webkitExitFullScreen) {
                document.webkitExitFullScreen()
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen()
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen()
            }
        },

        /** Mirrors tutorial.vue's handleFullscreenChange exactly. */
        handleFullscreenChange() {
            const isInFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.webkitCurrentFullScreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            )
            this.isFullscreen = isInFullscreen
            this.css_fullscreen_controls = isInFullscreen ? 'fullscreen-controls' : ''
            this.css_video_progressbar = isInFullscreen ? 'dynamicvideoProgressBar' : ''
        },

        // ── Modal helpers ─────────────────────────────────────────────────

        /** Only allow dismissing the overlay after the video is complete. */
        onOverlayClick() {
            if (this.videoCompleted) {
                this.$emit('close')
            }
        },
    },

    beforeDestroy() {
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
        if (this.isDragging) {
            document.removeEventListener('mousemove', this.onDrag)
            document.removeEventListener('mouseup', this.stopDrag)
        }
        if (this._volumeHideTimer) {
            clearTimeout(this._volumeHideTimer)
        }
    },
}
</script>

<style scoped lang="scss">
/* CSS class names mirror tutorial.vue for visual parity */

.ulvm-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.82);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
}

.ulvm-container {
    position: relative;
    width: 90%;
    max-width: 800px;
    background: var(--secondary-background);
    border-radius: var(--border-radius-primary);
    overflow: hidden;
    padding: 0 0 12px 0;
}

.ulvm-close-row {
    display: flex;
    justify-content: flex-end;
    padding: 8px 12px 4px;
}

.ulvm-close-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: var(--secondary-text);
    transition: color 0.2s;
    line-height: 1;

    &:hover {
        color: var(--primary-text);
    }
}

/* Remove border-radius on the Vimeo iframe */
.video-wrapper::v-deep iframe {
    display: block;
    width: 100%;
    border-radius: 0 !important;
}

/* ── Controls bar — identical to tutorial.vue ── */

.videoControls {
    width: 100%;
    background-color: var(--secondary-background);
    padding: 0px 10px;
    min-height: 44px;
}

/* Reduced from 15px to keep the bar compact and buttons vertically centered */
.volumeControls {
    padding: 6px 5px;
    display: flex;
    align-items: center;
}

.videoDurationWatched {
    width: 15px;
    flex-shrink: 0;
}

/* Flex context so the inner <p> inherits cross-axis centering */
.videoDurationWidth {
    min-width: 60px;
    display: flex;
    align-items: center;
}

/* Prevent <p> default margin/line-height from pushing text off-axis */
.videoDuration {
    font-size: 14px;
    line-height: 1;
    margin-bottom: 0 !important;
    align-self: center;
    white-space: nowrap;
}

.videoProgressBar {
    display: flex;
    align-items: center;
    margin: 0px 10px;
    width: 100%;
}

.rewindButton {
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding-top: 6px !important;
}

.playButton {
    padding-top: 6px !important;
}

.fullscreen-button {
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
    width: auto;
    min-width: 45px;
    padding-top: 5px !important;
}

/* Fullscreen overlay positioning — identical to tutorial.vue */
.fullscreen-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 9999;
    width: 100%;
}

.dynamicvideoProgressBar {
    width: 100% !important;
}

/* ── Volume slider — identical to tutorial.vue ── */

.volume-container {
    position: relative;
}

.volume-slider-container {
    position: absolute;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.8em 1em;
    width: 3.2em;
    height: 9.6em;
    margin-bottom: -0.6em;
    background: var(--secondary-background);
    border-radius: var(--border-radius-secondary) !important;
    bottom: 60px;
    left: 0px;
    outline: none;
    z-index: 10;
}

.custom-volume-slider {
    position: relative;
    width: 8px;
    height: 102px;
    cursor: pointer;
    user-select: none;
}

.slider-track {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: var(--primary-background);
    border-radius: 4px;
    top: 0;
    left: 0;
}

.slider-fill {
    position: absolute;
    width: 100%;
    background-color: var(--primary-color);
    border-radius: 4px;
    bottom: 0;
    left: 0;
    transition: height 0.1s ease;
}

.slider-thumb {
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: var(--primary-color);
    border-radius: 50%;
    left: -4px;
    transform: translateY(50%);
    cursor: grab;
    transition: bottom 0.1s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

    &:hover {
        transform: translateY(50%) scale(1.1);
    }

    &:active {
        cursor: grabbing;
        transform: translateY(50%) scale(1.2);
    }
}

/* ── Notice text ── */

.ulvm-notice {
    text-align: center;
    font-size: 13px;
    color: var(--secondary-text);
    margin: 8px 0 0;
    padding: 0 16px;
}

/* ── Responsive (mirrors tutorial.vue media queries) ── */

@media (max-width: 600px) {
    .ulvm-container {
        width: 98%;
    }

    .videoControls {
        padding: 15px 1px;
    }

    .volumeControls {
        padding: 4px 2px !important;
    }

    .videoDurationWatched {
        width: 20px !important;
    }

    .rewindButton {
        margin-left: 5px !important;
    }

    .videoProgressBar {
        display: none;
    }

    .videoDuration {
        text-align: center;
        width: 100%;
    }

    .videoDurationWidth {
        width: 100%;
    }

    .autoplay-button {
        padding: 0px 0px !important;
        min-width: none !important;
    }
}

@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
    .videoControls {
        padding: 0px 24px;
    }
}
</style>
